function normalizeIsActiveValue(raw, defaultValue = true) {
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "number") return raw !== 0;
  if (typeof raw === "string") {
    const value = raw.trim().toLowerCase();
    if (["false", "0", "inactive", "disabled", "off", "no"].includes(value)) {
      return false;
    }
    if (["true", "1", "active", "enabled", "on", "yes"].includes(value)) {
      return true;
    }
  }
  return defaultValue;
}

function hasExplicitIsActiveValue(row, expected) {
  if (!row || typeof row !== "object") return false;
  return ["isActive", "active", "enabled", "status"].some((field) => {
    if (!(field in row)) return false;
    return normalizeIsActiveValue(row[field], undefined) === expected;
  });
}

export function normalizeZoneIsActive(row, defaultValue = true) {
  if (!row || typeof row !== "object") return defaultValue;
  if (hasExplicitIsActiveValue(row, false)) return false;
  if ("isActive" in row) {
    return normalizeIsActiveValue(row.isActive, defaultValue);
  }
  if ("active" in row) {
    return normalizeIsActiveValue(row.active, defaultValue);
  }
  if ("enabled" in row) {
    return normalizeIsActiveValue(row.enabled, defaultValue);
  }
  if ("status" in row) {
    return normalizeIsActiveValue(row.status, defaultValue);
  }
  return defaultValue;
}

/**
 * GET /access-system/zones may return device rows as flat fields or nested
 * `{ workspaceId, deviceName?, device: { uuid, type, additionalData, … } }`.
 *
 * @param {object} x
 * @returns {{
 *   deviceId: string,
 *   workspaceId: string,
 *   deviceType: string,
 *   isActive: boolean,
 *   displayName: string,
 *   device: object|null,
 * } | null}
 */
export function normalizeZoneDeviceEntryFromApi(x) {
  if (!x || typeof x !== "object") return null;
  const nested = x.device && typeof x.device === "object" ? x.device : null;
  const deviceId = String(
    x.deviceId ?? nested?.uuid ?? nested?.deviceId ?? nested?.id ?? "",
  ).trim();
  const workspaceId = String(x.workspaceId ?? "").trim();
  const fromAdditional =
    nested?.additionalData &&
    typeof nested.additionalData.turnstileType === "string"
      ? nested.additionalData.turnstileType.trim()
      : "";
  const deviceType =
    String(
      fromAdditional ||
        x.deviceType ||
        nested?.deviceType ||
        x.type ||
        nested?.type ||
        "",
    ).trim() || "turnstile";
  const isActive =
    hasExplicitIsActiveValue(x, false) || hasExplicitIsActiveValue(nested, false)
      ? false
      : normalizeZoneIsActive(
          x,
          nested ? normalizeZoneIsActive(nested, true) : true,
        );
  const displayName =
    typeof x.deviceName === "string" && x.deviceName.trim() !== ""
      ? x.deviceName.trim()
      : typeof nested?.name === "string" && nested.name.trim() !== ""
        ? nested.name.trim()
        : "";
  if (!deviceId || !workspaceId) return null;
  return { deviceId, workspaceId, deviceType, isActive, displayName, device: nested };
}
